import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { Badge } from 'components/ui/badge';
import { ieltsHeadline, scopeLabel } from '../../constants';
import type { ExamReport } from '../../types';
import { GradingPendingStatus } from './GradingPendingStatus';

const band = (value: number | null | undefined) => (typeof value === 'number' ? value.toFixed(1) : '—');

interface SectionRow {
  key: string;
  label: string;
  detail: string;
  value: string;
  pending?: boolean;
}

/**
 * The results-form summary in the report's own terms: the overall IELTS band
 * (or SAT total) as the big number, a neutral CEFR pill, then one divided row
 * per section — the same rhythm as "Mavzular bo'yicha" for regular tests.
 */
export function ExamScoreSummary({
  report,
  className,
  onRefresh,
  refreshing,
}: {
  report: ExamReport;
  className?: string;
  /** Re-reads the report while a Writing or Speaking band is still being checked. */
  onRefresh?: () => void;
  refreshing?: boolean;
}) {
  const { t } = useTranslation();
  const ielts = report.standard === 'IELTS';

  const rows: SectionRow[] = ielts
    ? ([
        report.listening && {
          key: 'listening',
          label: t('exam.kind.listening'),
          value: band(report.listening.band),
          detail: t('exam.report.raw_of', { raw: report.listening.raw, max: report.listening.max }),
        },
        report.reading && {
          key: 'reading',
          label: t('exam.kind.reading'),
          value: band(report.reading.band),
          detail: t('exam.report.raw_of', { raw: report.reading.raw, max: report.reading.max }),
        },
        report.writing && {
          key: 'writing',
          label: t('exam.kind.writing'),
          value: report.writing.pending ? '…' : band(report.writing.band),
          detail: report.writing.pending ? t('exam.report.checking') : t('exam.report.two_tasks'),
          pending: report.writing.pending,
        },
        report.speaking && {
          key: 'speaking',
          label: t('exam.kind.speaking'),
          value: report.speaking.pending ? '…' : band(report.speaking.band),
          detail: report.speaking.pending
            ? t('exam.report.checking')
            : t('exam.report.speaking_answers', { count: report.speaking.recordings }),
          pending: report.speaking.pending,
        },
      ].filter(Boolean) as SectionRow[])
    : ([
        report.rw && { key: 'rw', label: t('exam.kind.sat_rw'), value: String(report.rw.scaled), detail: '200–800' },
        report.math && { key: 'math', label: t('exam.kind.sat_math'), value: String(report.math.scaled), detail: '200–800' },
      ].filter(Boolean) as SectionRow[]);

  // A Listening or Reading part leads with its raw score, so the per-section row would only repeat it.
  const lead = report.standard === 'IELTS' ? ieltsHeadline(report) : null;
  const scoped = lead?.kind === 'RAW';
  const headline = lead ? (lead.kind === 'RAW' ? lead.raw : lead.band) : report.standard === 'SAT' ? report.total : null;
  const headlineLabel = !lead
    ? t('exam.report.total')
    : lead.kind === 'OVERALL'
      ? t('exam.report.overall')
      : lead.kind === 'SKILL'
        ? t('exam.report.skill_band', { scope: scopeLabel(t, lead.scope) })
        : t('exam.report.part_score', { scope: scopeLabel(t, lead.scope) });
  const pendingLabel = lead?.kind === 'OVERALL' || !lead ? t('exam.report.overall_pending') : t('exam.report.skill_pending');
  const visibleRows = scoped ? [] : rows;

  return (
    <section aria-label={ielts ? t('exam.report.ielts_title') : t('exam.report.sat_title')} className={cn('flex flex-col', className)}>
      <div className="flex items-end justify-between gap-3 border-b border-border pb-[18px]">
        <span className="flex min-w-0 flex-col gap-1.5">
          <span className="text-[12.5px] font-medium text-muted-foreground">{headlineLabel}</span>
          {headline !== null && headline !== undefined ? (
            <span className="font-display text-[44px] font-bold leading-none tracking-[-0.03em] tabular-nums text-foreground">
              {lead?.kind === 'RAW' ? `${lead.raw}/${lead.max}` : lead ? band(headline) : headline}
            </span>
          ) : (
            <GradingPendingStatus className="text-foreground" label={pendingLabel} onRefresh={onRefresh} refreshing={refreshing} />
          )}
        </span>
        {lead?.kind === 'RAW' ? (
          <span className="shrink-0 text-[13px] tabular-nums text-muted-foreground">{lead.percent}%</span>
        ) : ielts && report.cefr ? (
          <Badge size="md" className="shrink-0">CEFR {report.cefr}</Badge>
        ) : !ielts ? (
          <span className="shrink-0 text-[13px] text-muted-foreground">{t('exam.report.total_range')}</span>
        ) : null}
      </div>

      {visibleRows.length > 0 && (
        <ul className="border-b border-border">
          {visibleRows.map((row) => (
            <li
              key={row.key}
              className="grid min-h-10 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 border-t border-border py-2 first:border-t-0"
            >
              <span className="flex min-w-0 items-baseline gap-2">
                <span className="truncate text-[13.5px] text-foreground">{row.label}</span>
                <span className="truncate text-xs tabular-nums text-muted-foreground">{row.detail}</span>
              </span>
              <span
                className={cn(
                  'text-right text-[13px] font-semibold tabular-nums',
                  row.pending ? 'text-muted-foreground' : 'text-foreground',
                )}
              >
                {row.value}
              </span>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
        {lead?.kind === 'RAW'
          ? t('exam.report.part_note')
          : ielts
            ? t('exam.report.disclaimer_ielts', { version: report.tableVersion })
            : t('exam.report.disclaimer_sat', { version: report.tableVersion })}
      </p>
    </section>
  );
}

export default ExamScoreSummary;
